import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Crown, Calendar } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const WelcomeBanner = ({ className = '' }) => {
  const { t } = useTranslation();
  const { user } = useAuth(); // Detectar mudanças no usuário
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Recarregar quando usuário muda (após reativação, cancelamento, upgrade)
    if (!user?.id) return;
    fetchStatus();
  }, [user]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/subscription/usage-status', {validateStatus: s => s === 200 || s === 401});
      if (response.status === 401) return; // Não logado, ignorar
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscriptionData) return null;

  const { subscription } = subscriptionData;
  
  // REGRA: Mostrar APENAS se:
  // 1. Status é 'trial' (não 'active', 'free', 'cancelled')
  // 2. Não foi cancelado
  // 3. trial_ends_at existe E está no futuro
  if (subscription.status !== 'trial') {
    return null;
  }
  
  // Don't show if subscription was cancelled (period of grace)
  if (subscription.cancelled_at) {
    return null;
  }

  const trialEnd = subscription.trial_ends_at;
  if (!trialEnd) {
    return null;
  }
  
  // NOVO: Verificar se trial ainda não acabou
  const endDate = new Date(trialEnd);
  const now = new Date();
  if (endDate <= now) {
    // Trial já acabou, não mostrar banner
    return null;
  }

  const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

  return (
    <Card className={`relative overflow-hidden border-0 shadow-2xl ${className}`}>
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24 blur-3xl"></div>
      
      {/* Content */}
      <div className="relative p-5 md:p-6">
        <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-4">
          {/* Welcome Message */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-white animate-pulse" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                SEJA MUITO BEM-VINDO!
              </h2>
            </div>
            
            <p className="text-white/90 text-base mb-3">
              Você está aproveitando o <strong>acesso Premium completo</strong> durante seu período de teste.
            </p>
            
            <div className="flex items-center justify-center md:justify-start gap-2 text-white/90">
              <Calendar className="w-5 h-5" />
              <p className="text-sm">
                Seu trial termina em: <strong className="text-white text-base">
                  {endDate.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </strong>
              </p>
            </div>
            
            <p className="text-white/75 text-xs mt-2">
              ({daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'} restantes para explorar tudo!)
            </p>
          </div>

          {/* Trial Badge */}
          <div className="flex-shrink-0">
            <div className="bg-white/20 backdrop-blur-md border-2 border-white/40 rounded-2xl p-4 shadow-xl">
              <div className="flex flex-col items-center text-center">
                <Crown className="w-8 h-8 text-white mb-2 drop-shadow-lg" />
                <Badge className="bg-white text-orange-600 font-bold text-sm px-3 py-1 mb-2 shadow-lg">
                  TRIAL
                </Badge>
                <p className="text-white font-bold text-xl mb-1">7 Dias</p>
                <p className="text-white/90 text-xs font-medium">GRÁTIS</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Highlights */}
        <div className="mt-4 pt-4 border-t border-white/30">
          <p className="text-white/90 text-xs text-center md:text-left">
            ✨ Aproveite: <strong>Receitas ilimitadas</strong> • <strong>IA ilimitada</strong> • <strong>Planejador semanal</strong> • <strong>Assistente de voz</strong>
          </p>
        </div>
      </div>
    </Card>
  );
};

export default WelcomeBanner;
